import { thumb } from "../lib/site";
import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MessageCircle, Check } from "lucide-react";
import SEO from "../components/SEO";
import PricingProof from "../components/PricingProof";
import { serviceSchema, faqSchema } from "../data/schema";
import { Reveal, Stagger, Item, Chapter, MaskedLine } from "../components/Reveal";
import { openLeadPopup } from "../components/Navbar";
import { SERVICES, PROJECTS } from "../data/content";
import { waLink, CONTEXT_MESSAGES } from "../lib/whatsapp";
import { track } from "../lib/analytics";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function ServicePage() {
  const { pathname } = useLocation();
  const service = SERVICES.find((s) => s.slug === pathname.replace(/\/+$/, "").slice(1));
  if (!service) return <Navigate to="/" replace />;
  const slug = service.slug;

  const related = PROJECTS.filter((p) =>
    p.services.some((ps) =>
      service.title.toLowerCase().includes(ps.split(" ")[0].toLowerCase()) ||
      ps.toLowerCase().includes(service.title.split(" ")[0].toLowerCase())
    )
  ).slice(0, 2);
  const relatedProjects = related.length ? related : PROJECTS.slice(0, 2);

  const schema = [
    serviceSchema({ name: service.heroTitle, description: service.heroDesc, path: `/${service.slug}`, serviceType: service.title }),
    faqSchema(service.faqs),
  ];

  return (
    <main className="pt-36 lg:pt-44">
      <SEO
        title={`${service.heroTitle} | Grotech Web`}
        description={service.heroDesc}
        path={`/${service.slug}`}
        schema={schema}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.title, path: `/${service.slug}` }]}
      />

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="service-hero">
        <nav className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <span className="mx-2 text-ember">/</span>
          <span className="text-white/70">{service.title}</span>
        </nav>
        <h1 className="mt-8 max-w-4xl font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskedLine>{service.heroTitle}</MaskedLine>
        </h1>
        <Reveal hero delay={0.3}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
            {service.heroDesc}
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <button
              data-testid="service-primary-cta"
              onClick={() => { track("cta_click", { location: "service", service: slug }); openLeadPopup(); }}
              className="group flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
            >
              Get a Free Website Plan
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={waLink(CONTEXT_MESSAGES[service.waKey] || CONTEXT_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="service-whatsapp-cta"
              onClick={() => track("whatsapp_click", { location: "service_page", service: slug })}
              className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#25D366] transition-colors hover:bg-[#25D366]/10"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <Chapter index="01" label="What You Get" />
        <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {service.benefits.map((b) => (
            <Item key={b.h} className="rounded-2xl border border-white/10 bg-ink p-7">
              <Check className="h-5 w-5 text-ember" />
              <h2 className="mt-4 font-display text-lg font-bold tracking-tight">{b.h}</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/50">{b.p}</p>
            </Item>
          ))}
        </Stagger>
      </section>

      <section className="bg-bone py-20 text-obsidian lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="02" label="The Process" dark />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {service.process.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-obsidian/10 bg-white p-7">
                  <span className="font-mono text-xs tracking-[0.2em] text-ember">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-3 font-display text-lg font-bold tracking-tight">{p.t}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-obsidian/60">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Chapter index="03" label="Questions" />
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Asked Before <span className="text-white/35">Every Project.</span>
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-white/50">
                Something more specific to your business? Ask directly on WhatsApp — you'll get a
                straight answer, not a sales script.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid="service-faq">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-display text-base font-semibold text-white hover:text-ember hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-white/55">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-ink py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Chapter index="04" label="Related Work" />
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {relatedProjects.map((p) => (
              <Reveal key={p.slug}>
                <Link
                  to={`/projects/${p.slug}/`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-obsidian sm:flex-row"
                >
                  <img
                    src={thumb(p.image)}
                    alt={`${p.name} project`}
                    loading="lazy"
                    className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-auto sm:w-2/5"
                  />
                  <div className="flex flex-1 flex-col justify-between p-6">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">{p.industry}{p.sample ? " · Sample" : " · Live"}</p>
                      <h3 className="mt-2 font-display text-xl font-bold tracking-tight">{p.name}</h3>
                      <p className="mt-2 text-sm text-white/50">{p.objective}</p>
                    </div>
                    <span className="mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ember">
                      View Case Study <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <PricingProof heading="Pricing" showProjects={false} />
    </main>
  );
}
